import { Controller } from '../utils/controller';
import { clamp } from '../utils/numbers';
import { InventoryStatus } from '../utils/inventoryStatuses';
import getPerlgemCSRFToken from '../utils/perlgem_csrf';
import { sanitizePayload } from '../utils/cart';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    site: any;
  }
}

export interface IProductController {
  hasAutoReplenishSelectorTarget: boolean;
  autoReplenishSelectorTarget: unknown;
}

export default class extends Controller implements IProductController {
  declare readonly hasCustomerReviewsContainerTarget: boolean;
  declare customerReviewsContainerTarget?: HTMLElement;

  static targets = [
    'addButton',
    'altImage',
    'autoReplenish',
    'autoReplenishFrequency',
    'autoReplenishInformation',
    'autoReplenishSelector',
    'decreaseButton',
    'dropdown',
    'incrementButton',
    'ingredients',
    'inventoryStatusLabel',
    'loadingButton',
    'price',
    'priceInput',
    'priceWithDiscount',
    'primaryImage',
    'productURL',
    'productBadge',
    'productImage',
    'quantity',
    'rating',
    'shadeTitle',
    'size',
    'sizeDropdown',
    'sizeDropdownCaret',
    'sizeSelector',
    'skuBadge',
    'skuButton',
    'toggleIcon',
    'toggleQnA',
    'customerReviewsContainer',
    'pricePerUnit',
    'engravingText',
    'engravingType',
    'loyaltyDynamicCTA'
  ];
  static requiredTargets = [];

  static values = {
    activeVariant: Object,
    addToBagLoadingText: {
      type: String,
      default: 'Loading ...'
    },
    addToBag: String,
    alterUrlOnChange: Boolean,
    backorder: Boolean,
    badgeText: String,
    comingSoon: {
      type: String,
      default: 'Coming Soon'
    },
    currency: String,
    currencycode: String,
    currencyWithDiscount: String,
    id: String,
    isShoppable: {
      type: Boolean,
      default: true
    },
    outOfStock: String,
    price: String,
    priceWithDiscount: String,
    product: Object,
    productid: String,
    data: Object,
    rating: String,
    selectedsku: String,
    size: String,
    pricePerUnit: String,
    skus: Array,
    soldOut: String,
    quantity: {
      type: Number,
      default: 1
    },
    minQuantity: {
      type: Number,
      default: 1
    },
    maxQuantity: {
      type: Number,
      default: 3
    },
    url: String
  };

  static classes = [
    'decreaseDisabled',
    'decreaseEnabled',
    'disabled',
    'discount',
    'enabled',
    'hidden',
    'incrementDisabled',
    'incrementEnabled',
    'nowrap',
    'priceStrikethrough'
  ];
  static requiredClasses = [];

  // TARGETS
  declare addButtonTarget: HTMLButtonElement;
  declare loyaltyDynamicCTATarget: HTMLButtonElement;
  declare addButtonTargets: HTMLButtonElement[];
  declare altImageTarget: HTMLImageElement;
  declare autoReplenishFrequencyTarget: HTMLSpanElement;
  declare autoReplenishInformationTarget: HTMLElement;
  declare autoReplenishSelectorTarget: HTMLDivElement;
  declare autoReplenishTarget: HTMLDivElement;
  declare autoReplenishTargets: HTMLDivElement[];
  declare dropdownTargets: HTMLDivElement;
  declare decreaseButtonTarget: HTMLElement;
  declare readonly hasAddButtonTarget: boolean;
  declare readonly hasAltImageTarget: boolean;
  declare readonly hasAutoReplenishInformationTarget: boolean;
  declare readonly hasAutoReplenishSelectorTarget: boolean;
  declare readonly hasAutoReplenishTarget;
  declare readonly hasDecreaseButtonTarget: boolean;
  declare readonly hasIncrementButtonTarget: boolean;
  declare readonly hasIngredientsTarget: boolean;
  declare readonly hasInventoryStatusLabelTarget: boolean;
  declare readonly hasLoadingButtonTarget: boolean;
  declare readonly hasPriceInputTarget: boolean;
  declare readonly hasPriceTarget: boolean;
  declare readonly hasPriceWithDiscountTarget: boolean;
  declare readonly hasPrimaryImageTarget: boolean;
  declare readonly hasProductImageTarget: boolean;
  declare readonly hasProductURLTarget: boolean;
  declare readonly hasQuantityTarget: boolean;
  declare readonly hasRatingTarget: boolean;
  declare readonly hasShadeTitleTarget: boolean;
  declare readonly hasSizeDropdownTarget: boolean;
  declare readonly hasSizeTarget: boolean;
  declare readonly hasSkuBadgeTarget: boolean;
  declare readonly hasToggleQnATarget: boolean;
  declare readonly hasPricePerUnitTarget: boolean;
  declare readonly hasLoyaltyDynamicCTATarget: boolean;
  declare incrementButtonTarget: HTMLElement;
  declare ingredientsTarget: HTMLElement;
  declare inventoryStatusLabelTarget: HTMLElement;
  declare loadingButtonTarget: HTMLElement;
  declare priceInputTarget: HTMLInputElement;
  declare priceTarget: HTMLElement;
  declare priceWithDiscountTarget: HTMLElement;
  declare primaryImageTarget: HTMLImageElement;
  declare productImageTargets: HTMLImageElement[];
  declare productURLTargets: HTMLAnchorElement[];
  declare quantityTarget;
  declare ratingTargets: HTMLDivElement[];
  declare shadeTitleTarget;
  declare sizeDropdownCaretTarget: HTMLElement;
  declare sizeDropdownTarget: HTMLDivElement;
  declare sizeTarget: HTMLSpanElement;
  declare skuBadgeTarget: HTMLSpanElement;
  declare toggleIconTargets: HTMLSpanElement[];
  declare toggleQnATarget: HTMLDivElement;
  declare pricePerUnitTarget: HTMLSpanElement;
  declare engravingTextTarget;
  declare engravingTypeTarget;

  // VALUES
  declare addToBagLoadingTextValue;
  declare addToBagValue: string;
  declare alterUrlOnChangeValue: boolean;
  declare autoReplenishFrequencyValue;
  declare backorderValue: boolean;
  declare badgeTextValue;
  declare comingSoonValue: string;
  declare currencyValue: string;
  declare currencyWithDiscountValue: string;
  declare dataValue: Record<string, unknown>;
  declare iframeId: string;
  declare isShoppableValue: boolean;
  declare maxQuantityValue: number;
  declare minQuantityValue: number;
  declare outOfStockValue: string;
  declare overlaytimeoutValue: number | undefined;
  declare priceValue: number;
  declare quantityValue: number;
  declare ratingValue: number;
  declare selectedskuValue: string;
  declare sizeValue: string;
  declare skusValue: string;
  declare soldOutValue: string;
  declare urlValue: string;
  declare pricePerUnitValue: string;
  declare updateProductVariantImageValue: boolean;

  // CLASSES
  declare disabledClasses: string[];
  declare discountClasses: string[];
  declare enabledClasses: string[];
  declare hiddenClasses: string[];
  declare nowrapClasses: string[];
  declare priceStrikethroughClasses: string[];
  declare hiddenClass: string;
  declare decreaseDisabledClass: string;
  declare decreaseEnabledClass: string;
  declare incrementDisabledClass: string;
  declare incrementEnabledClass: string;

  // LOCAL STATE
  private autoReplenishFrequencyLabel: string;
  private lastTimeout: number;
  private questionAnswersInited: boolean;
  private observer: MutationObserver | null = null;

  initialize() {
    if (this.hasQuantityTarget) {
      this.quantityTarget.addEventListener('focusout', () => {
        const val = Number(this.quantityTarget.value);

        if (!val) {
          const newValue = clamp(val, this.minQuantityValue, this.maxQuantityValue);
          this.quantityTarget.value = newValue;
          this.quantityValue = newValue;
        }
      });
    }
    this.updateSkuIngredients(this.selectedskuValue);
  }

  altImageErrored = () => {
    this.altImageTarget.src = this.primaryImageTarget.src;
    this.altImageTarget.alt = this.primaryImageTarget.alt;
  };

  primaryImageError = () => {
    if (this.hasPrimaryImageTarget) {
      const div = document.createElement('div');
      div.classList.add('aspect-square');

      this.hasAltImageTarget && this.altImageTarget.remove();
      this.primaryImageTarget.replaceWith(div);
    }
  };

  priceValueChanged() {
    if (this.hasPriceInputTarget) {
      this.priceInputTarget.value = String(Math.round(this.priceValue));
    }
  }

  sizeValueChanged() {
    if (this.hasSizeTarget) {
      this.sizeTarget.innerText = this.sizeValue;
    }
  }

  currencyValueChanged() {
    if (this.currencyValue === '') {
      return;
    }

    if (this.hasPriceTarget) {
      this.priceTarget.innerText = this.currencyValue;
    }
  }

  currencyWithDiscountValueChanged() {
    if (this.currencyWithDiscountValue === '') {
      return;
    }

    if (this.hasPriceWithDiscountTarget && this.currencyWithDiscountValue !== this.currencyValue) {
      this.priceWithDiscountTarget.innerText = this.currencyWithDiscountValue;
    }
  }

  dispatchEvent(name, payload) {
    this.dispatch(name, { detail: payload });
  }

  ratingValueChanged() {
    if (this.hasRatingTarget) {
      this.ratingTargets.forEach((item) => {
        item.setAttribute('style', `--rating: ${this.ratingValue}`);
        item.ariaLabel = `Rating of this product is ${this.ratingValue} out of 5.`;
      });
    }
  }

  quantityValueChanged() {
    if (this.hasQuantityTarget) {
      this.quantityTarget.value = this.quantityValue;
    }
    if (this.hasDecreaseButtonTarget) {
      this.decreaseButtonTarget.classList.toggle(
        this.decreaseDisabledClass,
        this.quantityValue <= this.minQuantityValue
      );
      this.decreaseButtonTarget.classList.toggle(
        this.decreaseEnabledClass,
        this.quantityValue > this.minQuantityValue
      );
    }

    if (this.hasIncrementButtonTarget) {
      this.incrementButtonTarget.classList.toggle(
        this.incrementEnabledClass,
        this.quantityValue < this.maxQuantityValue
      );
      this.incrementButtonTarget.classList.toggle(
        this.incrementDisabledClass,
        this.quantityValue >= this.maxQuantityValue
      );
    }
  }

  updateQuantityValue(event) {
    const rawVal = event.target.value || event.detail?.value;
    const val = Number(rawVal);

    if (val) {
      const newValue = clamp(val, this.minQuantityValue, this.maxQuantityValue);
      this.quantityValue = newValue;

      if (this.hasQuantityTarget) {
        this.quantityTarget.value = newValue;
      }
    }
  }

  updateSelectedAutoReplenishFrequency(frequency) {
    this.autoReplenishFrequencyValue = frequency;
  }

  updateSelectedSku(newSkuId) {
    this.selectedskuValue = newSkuId;
    this.updateSkuIngredients(newSkuId);
  }

  updateSelectedSkuViaEvent(event) {
    const skuid = event.currentTarget.dataset.skuid;
    this.updateSelectedSku(skuid);
  }

  updateSkuIngredients(newSkuId) {
    if (this.hasIngredientsTarget) {
      this.ingredientsTarget.innerText = this.getIngredientsForSku(newSkuId);
    }
  }

  isValidIngredients(ingredients: string | null): boolean {
    return !!ingredients;
  }

  getIngredientsForSku(skuId) {
    const variants = this.getVariants();
    const sku = variants?.find((sku) => {
      return (
        (sku.perlgem_sku_id == parseInt(skuId) || sku.id == skuId) &&
        this.isValidIngredients(sku.ingredients)
      );
    });

    return sku?.ingredients || '';
  }

  handleAutoReplenishSelect(event) {
    this.updateSelectedAutoReplenishFrequency(event.target.value);

    if (event.target.dataset.name) {
      this.autoReplenishFrequencyTarget.innerText = event.target.dataset.name;
    }

    this.hasAutoReplenishTarget && this.autoReplenishTarget.classList.toggle('hidden');
  }

  handleProductClick() {
    const productClickEvent = new CustomEvent('analytics.productClick', {
      detail: {
        productId: this.productidValue
      }
    });
    window.dispatchEvent(productClickEvent);
  }

  handleShadeRadioSelect(e) {
    this.updateSelectedSku(e.target.value);
  }

  handleShadeSelect(event) {
    this.updateSelectedSku(event.params.skuId);
  }

  handleSizeButton(event) {
    event.target.dataset.sku && this.updateSelectedSku(event.target.dataset.sku);
  }

  handleSizeSelect(event) {
    this.updateSelectedSku(event.target.value);
    if (event.target.dataset.name) {
      this.sizeTarget.innerText = event.target.dataset.name;
    }

    this.hasAutoReplenishSelectorTarget &&
      this.autoReplenishSelectorTarget.classList.toggle(
        'hidden',
        event.target.dataset.isReplenishable
      );

    if (this.hasSizeDropdownTarget) {
      this.sizeDropdownTarget.classList.toggle('hidden');
    }
  }

  handleVariantUpdate() {
    const product = JSON.parse(this.element.dataset.productValue || '{}');
    const variant = JSON.parse(this.element.dataset.activeVariantValue || '{}');

    const variantUpdateEvent = new CustomEvent('analytics.variantUpdatePDPEvent', {
      detail: {
        payload: {
          product,
          variant
        }
      }
    });

    window.dispatchEvent(variantUpdateEvent);
  }

  connect() {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-active-variant-value'
        ) {
          this.handleVariantUpdate();
        }
      }
    });

    this.observer.observe(this.element, {
      attributes: true,
      attributeFilter: ['data-active-variant-value']
    });
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  updateCTAStatus(disabled: boolean, status: string, label: string) {
    if (this.hasAddButtonTarget) {
      this.addButtonTargets.forEach((target) => {
        if (disabled) {
          target.classList.add(...this.disabledClasses);
          target.classList.remove(...this.enabledClasses);
        } else {
          target.classList.remove(...this.disabledClasses);
          target.classList.add(...this.enabledClasses);
        }

        target.disabled = disabled;
        target.innerText = status;
      });

      if (this.hasInventoryStatusLabelTarget) {
        this.inventoryStatusLabelTarget.innerText = label;
      }
    }
  }

  getVariants() {
    if (Array.isArray(this.skusValue) && this.skusValue.length) {
      return this.skusValue;
    } else if (
      this.dataValue &&
      Array.isArray(this.dataValue.variants) &&
      this.dataValue.variants.length
    ) {
      return this.dataValue.variants;
    } else {
      return null;
    }
  }

  selectedskuValueChanged(value, prevValue) {
    const skus = this.getVariants();
    if (!skus) {
      return null;
    }
    const sku = skus.find((item) => item.perlgem_sku_id == value || item.id == value);
    let label = '';

    if (sku?.shade || sku?.shades) {
      const first_shade = sku?.shade || sku?.shades[0] || {};
      this.dispatchEvent('sku:changed', { sku });

      this.priceValue = sku.price;
      this.currencyValue = sku.price_with_currency_code;

      const shadeTitle = first_shade?.title;
      const imageAltSeparator = ' | ';
      const shadePrefix = shadeTitle ? `${shadeTitle}${imageAltSeparator}` : '';

      if (this.hasPricePerUnitTarget) {
        this.pricePerUnitTarget.innerText = sku.price_per_unit_description;
      }

      if (this.hasPrimaryImageTarget) {
        this.primaryImageTarget.src = sku.images?.[0]?.src;
        this.primaryImageTarget.alt = `${shadePrefix}${sku.images?.[0]?.alt}`.toLowerCase();
      }
      if (this.hasAltImageTarget && sku.images.length > 1) {
        this.altImageTarget.src = sku.images[1].src;
        this.altImageTarget.alt = `${shadePrefix}${sku.images[1].alt}`.toLowerCase();
      }

      if (this.hasSkuBadgeTarget) {
        this.skuBadgeTarget.innerText = sku.badge || this.badgeTextValue;
        this.skuBadgeTarget.classList.toggle('hidden', !this.skuBadgeTarget.innerText);
      }
      if (this.hasShadeTitleTarget) {
        this.shadeTitleTarget.innerText = first_shade.title;
      }

      if (this.hasProductURLTarget) {
        this.productURLTargets.forEach((el) => {
          if (el.dataset?.isSizedParam) {
            el.href = this.updateVariantUrl(this.urlValue, sku.size, 'size');
          } else {
            el.href = this.updateVariantUrl(this.urlValue, first_shade.title, 'shade');
          }
        });
      }

      if (this.alterUrlOnChangeValue && first_shade?.title) {
        this.updateUrlShadeValue(first_shade.title);
      }

      const addToBagLabel = this.addToBagValue;

      switch (sku.inventory_status) {
        case InventoryStatus.active:
          this.updateCTAStatus(false, addToBagLabel, '');
          break;
        case InventoryStatus.sold_out:
          this.updateCTAStatus(true, this.soldOutValue, '');
          break;
        case InventoryStatus.temp_out_of_stock:
          label = this.outOfStockValue || '';
          if (this.backorderValue) {
            this.updateCTAStatus(true, this.outOfStockValue, label);
          } else {
            this.updateCTAStatus(false, addToBagLabel, label);
          }
          break;
        case InventoryStatus.coming_soon:
          this.updateCTAStatus(true, this.comingSoonValue, '');
          break;
        default:
          break;
      }

      this.handleDiscount(sku);
      this.showSelectedProductImage(value, prevValue);
    }
  }

  updateUrlShadeValue(shadeName: string) {
    const currentURL = window.location.href;
    const url = new URL(currentURL);
    url.searchParams.set('shade', shadeName);
    history.pushState(null, '', url.toString());
  }

  handleDiscount(sku) {
    if (sku.is_discounted) {
      this.hasPriceTarget && this.priceTarget.classList.add(...this.priceStrikethroughClasses);
      if (this.hasPriceWithDiscountTarget) {
        this.priceWithDiscountTarget.innerText = sku.price_with_discount_with_currency_code;
        this.priceWithDiscountTarget.classList.remove(...this.hiddenClasses);
      }
      this.hasPriceWithDiscountTarget &&
        this.priceWithDiscountTarget.classList.remove(...this.hiddenClasses);
    } else {
      this.hasPriceTarget && this.priceTarget.classList.remove(...this.priceStrikethroughClasses);
      this.hasPriceWithDiscountTarget &&
        this.priceWithDiscountTarget.classList.add(...this.hiddenClasses);
    }
  }

  createDiscountElement(price) {
    const span = document.createElement('span');
    span.classList.add(...this.discountClasses);
    span.setAttribute('data-product-target', 'priceWithDiscount');
    span.innerText = price;
    return span;
  }

  incrementQuantity() {
    this.quantityValue = clamp(
      this.quantityValue + 1,
      this.minQuantityValue,
      this.maxQuantityValue
    );
  }

  decreaseQuantity() {
    this.quantityValue = clamp(
      this.quantityValue - 1,
      this.minQuantityValue,
      this.maxQuantityValue
    );
  }

  handleButtonLoading() {
    const isValidLoading =
      this.hasAddButtonTarget && this.addButtonTarget?.dataset?.showLoadingParam;

    if (isValidLoading) {
      if (this.hasLoadingButtonTarget) {
        this.loadingButtonTarget.classList.remove(this.hiddenClass);
        this.addButtonTarget.classList.add(this.hiddenClass);
      } else {
        this.addToBagValue = this.addButtonTarget.innerText;
        this.addButtonTarget.innerText = this.addToBagLoadingTextValue;
        this.addButtonTarget.disabled = true;
      }
    }
  }

  handleButtonLoaded() {
    const isValidLoading = this.hasAddButtonTarget;

    if (isValidLoading) {
      if (this.hasLoadingButtonTarget) {
        this.loadingButtonTarget.classList.add(this.hiddenClass);
        this.addButtonTarget.classList.remove(this.hiddenClass);
      } else {
        this.addButtonTarget.innerText = this.addToBagValue || this.addButtonTarget.innerText;
        if (this.isShoppableValue) {
          this.addButtonTarget.disabled = false;
        }
      }
    }
  }

  async addToCart({
    reusePayload = false,
    addEngraving = false
  }: { reusePayload?: boolean; addEngraving?: boolean } = {}) {
    this.handleButtonLoading();

    const token = await getPerlgemCSRFToken();
    const submit = this.autoReplenishFrequencyValue ? 'alter_replenishment' : 'cart';
    const autoReplenishParams = this.getAutoReplenishParams(this.autoReplenishFrequencyValue);
    const engravingParams = addEngraving ? this.getEngravingParams() : {};
    const normalizedSkuValue = Array.isArray(this.selectedskuValue)
      ? this.selectedskuValue
      : [this.selectedskuValue];
    const SKU_BASE_ID = this.autoReplenishFrequencyValue
      ? this.selectedskuValue
      : normalizedSkuValue;

    const params = [
      {
        _SUBMIT: submit,
        SKU_BASE_ID,
        INCREMENT: 1,
        QTY: this.quantityValue,
        _TOKEN: token,
        ...autoReplenishParams,
        ...engravingParams
      }
    ];

    const method = 'rpc.form';
    const url = `/rpc/jsonrpc.tmpl?dbgmethod=${method}`;
    const id = 1;
    const postobj = [
      {
        method,
        params,
        id
      }
    ];
    const formData = new URLSearchParams();
    formData.append('JSONRPC', JSON.stringify(postobj));
    document.cookie = `csrftoken=${token};path=/`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData
    });
    const responseJson = await response.json();
    if (addEngraving) {
      const errorMessage = responseJson[0]?.error?.data?.messages[0]?.text;
      if (errorMessage) {
        this.handleButtonLoaded();
        return this.element.dispatchEvent(
          new CustomEvent('engraving:error', {
            detail: {
              message: errorMessage
            },
            bubbles: true
          })
        );
      }
    }

    const responseData = response.ok ? responseJson : [];
    const rawCartData = responseData[0]?.result?.data?.trans_data;
    const responseMessage = responseData[0]?.result?.data?.messages?.[0]?.text;
    const responseErrorMessage = responseData[0]?.error?.data?.messages?.[0]?.text;
    const productsAddedToCart = responseData[0]?.result?.data?.ac_results;
    const isMultiAdd = Array.isArray(SKU_BASE_ID) ? SKU_BASE_ID.length > 1 : false;
    let itemsToAddCount = 0;

    if (rawCartData) {
      const eventPayload = {
        itemsCount: rawCartData.items_count,
        subtotal: rawCartData.formattedSubtotal
      };
      const rawCartItems = rawCartData?.order?.items;
      if (Array.isArray(rawCartItems)) {
        eventPayload['items'] = rawCartItems.map((item) => {
          return {
            title: item['prod.PROD_RGN_NAME'],
            subtitle: item['sku.label'],
            quantity: item.ITEM_QUANTITY,
            image: item['sku.SMALL_IMAGE'],
            price: item['sku.PRICE'],
            formattedAppliedPrice: item.formattedAppliedPrice,
            url: item['prod.url']
          };
        });
      }

      if (isMultiAdd && Array.isArray(productsAddedToCart)) {
        productsAddedToCart.map((item) => {
          if (item?.result?.SUCCESS) {
            itemsToAddCount++;
          }
        });
      }

      const atbResponseData = responseData[0] ?? {};
      const totalQuantityToAdd = isMultiAdd ? itemsToAddCount : this.quantityValue;
      const atbDetail = {
        cartMode: 'modal',
        skuId: SKU_BASE_ID,
        quantity: totalQuantityToAdd
      };

      if (reusePayload) {
        const {
          result: { data: payload }
        } = atbResponseData;
        const sanitizeResponseData = {
          ...atbResponseData,
          result: { data: sanitizePayload(payload) }
        };

        atbDetail['atbResponseData'] = sanitizeResponseData;
      }

      if (addEngraving && Array.isArray(productsAddedToCart)) {
        for (const item of productsAddedToCart) {
          if (item?.method && item?.method === 'save' && item?.result?.COLLECTION_ID) {
            atbDetail['collectionId'] = item?.result?.COLLECTION_ID;
            break;
          }
        }
      }

      if (responseErrorMessage) {
        atbDetail['blockUpdate'] = true;
        atbDetail['error'] = true;
        atbDetail['errorMessage'] = responseMessage || responseErrorMessage;
      }

      const newCartEvent = new CustomEvent('openCart', {
        detail: atbDetail
      });
      window.dispatchEvent(newCartEvent);

      const cartEvent = new CustomEvent('cartUpdated', {
        detail: eventPayload
      });
      window.dispatchEvent(cartEvent);

      const rpcResultEvent = new CustomEvent('RPC:RESULT', {
        detail: {
          responseData,
          requestArgs: postobj,
          requestId: id
        }
      });
      window.dispatchEvent(rpcResultEvent);

      const cartConfig = window?.site?.config?.cart;

      if (cartConfig && cartConfig?.enabled) {
        const reactCartEvent = new CustomEvent('cart.updated', {
          detail: {
            newDisplayState: !!cartConfig?.productAddedLabel,
            autoClose: true,
            response: { data: responseData },
            itemQty: rawCartData?.items_count,
            miniOverlay: cartConfig.miniOverlay
          }
        });
        document.dispatchEvent(reactCartEvent);
      }
    } else {
      this.handleError();
    }

    if (this.iframeId) {
      document
        .getElementById(this.iframeId)
        ?.iFrameResizer?.sendMessage(params[0], [window.document.location.origin]);
    } else {
      this.handleButtonLoaded();
    }
  }

  async addToCartV2() {
    this.handleButtonLoading();

    const skus = this.selectedskuValue;
    const quantity = this.quantityValue;

    const htmlCartResponse = await fetch('/cart-drawer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': this.csrfToken
      },
      body: JSON.stringify({ skus, quantity })
    })
      .then((response) => response.text())
      .catch(() => this.handleError());

    if (htmlCartResponse) {
      const newCartEvent = new CustomEvent('openCart', {
        detail: {
          html: htmlCartResponse,
          cartMode: 'modal',
          productId: this.productidValue || this.idValue,
          quantity: this.quantityValue,
          skuId: this.selectedskuValue
        }
      });
      window.dispatchEvent(newCartEvent);
    }
    this.handleButtonLoaded();
  }

  async addToCartV1_2() {
    this.handleButtonLoading();

    const skus = [this.selectedskuValue];
    const quantity = this.quantityValue || 1;
    const action = 'add';

    const htmlCartResponse = await fetch('/cart/v3_0/drawer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': this.csrfToken
      },
      body: JSON.stringify({ skus, quantity, action })
    })
      .then((response) => response.text())
      .catch(() => this.handleError());

    if (htmlCartResponse) {
      const newCartEvent = new CustomEvent('openCart', {
        detail: {
          html: htmlCartResponse,
          cartMode: 'modal',
          productId: this.productidValue || this.idValue,
          quantity: quantity,
          skuId: this.selectedskuValue
        }
      });
      window.dispatchEvent(newCartEvent);
    }
    this.handleButtonLoaded();
  }

  handleError() {
    const newCartEvent = new CustomEvent('openCart', {
      detail: {
        cartMode: 'modal',
        error: true
      }
    });
    window.dispatchEvent(newCartEvent);
  }

  getAutoReplenishParams = (autoReplenishFrequencyValue) => {
    if (autoReplenishFrequencyValue) {
      return {
        action: 'add',
        REPLENISHMENT_FREQ: autoReplenishFrequencyValue,
        add_to_cart: 1
      };
    }
    return {};
  };

  getEngravingParams = () => {
    return {
      _SUBMIT: 'alter_monogram',
      action: 'add,monogram,save',
      COLLECTION_NAME: `${this.selectedskuValue}:${this.engravingTypeTarget.value}`,
      COLLECTION_TYPE: 'MONO',
      COLLECTION_SUBTYPE: 'MONO',
      MONOGRAM_FONT: 'Kuenstler',
      MONOGRAM_TEXT: this.engravingTextTarget.value
    };
  };

  handleAddToCartClick(event) {
    const cartApi = event?.params?.cartApi || window.site?.config?.cart?.cartApi || 'v1';
    const reusePayload = event?.params?.reusePayload;
    const addEngraving = event?.params?.addEngraving;

    if (cartApi === 'v2') {
      this.addToCartV2();
    } else if (cartApi === 'v1_2') {
      this.addToCartV1_2();
    } else {
      this.addToCart({ reusePayload, addEngraving });
    }
    event.preventDefault();
  }

  handleAutoReplenish() {
    this.hasAutoReplenishInformationTarget &&
      this.autoReplenishInformationTarget.classList.toggle('hidden');
  }

  handleQuickshopClick(event) {
    event.preventDefault();
    const quickShopEvent = new CustomEvent('analytics.quickshop.clicked', {
      detail: {
        productId: this.productidValue
      }
    });
    window.dispatchEvent(quickShopEvent);
  }
  handleSignedInDynamicCTAClick = () => {
    const productURL =
      this.hasLoyaltyDynamicCTATarget && this.loyaltyDynamicCTATarget?.dataset?.localizedPathParam;

    window.location.href =
      '/account/profile_preferences.tmpl?loyalty_enrolled_source=member_access_signedin&RETURN_URL=' +
      productURL +
      '#profile_loyalty_section';
  };

  handleGuestDynamicCTAClick = () => {
    const productURL =
      this.hasLoyaltyDynamicCTATarget && this.loyaltyDynamicCTATarget?.dataset?.localizedPathParam;

    window.location.href =
      '/account/profile_preferences.tmpl?loyalty_enrolled_source=member_access_guest&RETURN_URL=' +
      productURL +
      '#new-account';
  };

  toggleAutoReplenish() {
    if (this.lastTimeout) {
      clearTimeout(this.lastTimeout);
    }

    this.lastTimeout = setTimeout(() => {
      this.autoReplenishTargets[0].classList.toggle('hidden');
    }, this.overlaytimeoutValue);
  }

  toggleDropdown(event) {
    const index = Number(event.currentTarget.dataset.index);

    for (const cl of this.nowrapClasses) {
      this.dropdownTargets[index].classList.toggle(cl);
    }

    let productcardExpanded = false;
    if (this.dropdownTargets[index].classList.contains(this.nowrapClasses[1])) {
      productcardExpanded = true;
      this.toggleIconTargets[index].innerText = '-';
    } else {
      this.toggleIconTargets[index].innerText = '+';
    }
    // Analytics Tagging For MTA-5849 start.
    const productCardEvent = new CustomEvent('pdp.accordion', {
      detail: {
        productCardAction: productcardExpanded,
        productCardLabel: event.currentTarget.dataset.productLabel
      }
    });
    window.dispatchEvent(productCardEvent);
    // Analytics Tagging For MTA-5849 end.
  }

  toggleSubscribe() {
    this.autoReplenishFrequencyLabel = this.autoReplenishFrequencyLabel
      ? this.autoReplenishFrequencyLabel
      : this.autoReplenishFrequencyTarget.innerText;
    this.autoReplenishFrequencyValue = null;
    this.autoReplenishFrequencyTarget.innerText = this.autoReplenishFrequencyLabel;
  }

  qnaShowIfValueChanged(value) {
    if (this.hasToggleQnATarget) {
      const target = this.toggleQnATarget;

      if (value) {
        target.style.display = 'block';

        if (!this.questionAnswersInited) {
          this.questionAnswersInited = true;
        } else {
          target.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
        }
      } else {
        target.style.display = 'none';
      }
    }
  }

  updateVariantUrl(url, title, param) {
    const value = encodeURI(title.replace(/ /g, '_'));
    const urlParts = url.split('?');
    const path = urlParts[0];
    const search = urlParts[1];
    const searchParams = new URLSearchParams(search);

    if (searchParams.has(param)) {
      searchParams.set(param, value);
    }

    return `${path}?` + searchParams.toString();
  }

  scrollToReviews() {
    this.hasCustomerReviewsContainerTarget &&
      this.customerReviewsContainerTarget?.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest'
      });
  }

  showSelectedProductImage(selectedImageId, prevImageId) {
    if (this.hasProductImageTarget) {
      this.productImageTargets.forEach((element) => {
        if (element.dataset.skuId === selectedImageId) {
          element.classList.remove('hidden');
        } else if (element.dataset.skuId === prevImageId) {
          element.classList.add('hidden');
        }
      });
    }
  }
}
